import { createContext, useContext, type ReactNode } from "react";

/**
 * THE one file the brief says an app replaces: "copied verbatim into packages/theme, with ONLY the
 * menu-items array replaced by our routes."
 *
 * The template's version is a static 60-entry array of its own demo routes. Ours cannot be static:
 * the nav is filtered by the live permission set (constitution rule 3 — permissions come from the
 * API's role-permission matrix, never a hard-coded role), which is only known at render time. So
 * the array arrives through a context the host app fills, and `sidebar-menu.tsx`'s single change
 * from the template is reading `useMenuItems()` where it read the imported `menuItems`.
 *
 * The item SHAPE is the template's, unchanged — an entry with no `href` is a group heading, an
 * entry with `dropdownItems` renders as a `Collapse`, `badge` renders a `StatusBadge`. All of
 * `sidebar-menu.tsx`'s markup keeps working because the data it walks is the same shape.
 */
export type MenuItem = {
  name: string;
  href?: string;
  icon?: ReactNode;
  badge?: string;
  dropdownItems?: { name: string; href: string; badge?: string }[];
};

const MenuItemsContext = createContext<MenuItem[]>([]);

export function MenuItemsProvider({ items, children }: { items: MenuItem[]; children: ReactNode }) {
  return <MenuItemsContext.Provider value={items}>{children}</MenuItemsContext.Provider>;
}

export const useMenuItems = () => useContext(MenuItemsContext);
